import type { ReactNode } from "react"

import { SalesIcon } from "./icons"

// Primitivas da tela de vendas. Página de marketing: tipografia própria com
// clamp() (desvio consciente do Fixed Scale Rule do DESIGN.md — mesma
// exceção já aberta pelo checkout) e paleta sales-* de globals.css.

const SHELL_TONE: Record<string, string> = {
  bg: "bg-background-50",
  alt: "bg-sales-bg-alt",
  ink: "bg-sales-ink",
  white: "bg-white",
}

export function Shell({
  children,
  tone = "bg",
  className = "",
  id,
}: {
  children: ReactNode
  tone?: "bg" | "alt" | "ink" | "white"
  className?: string
  id?: string
}) {
  return (
    <section id={id} className={`${SHELL_TONE[tone]} ${className}`}>
      <div className="mx-auto w-full max-w-[1180px] px-5 sm:px-8 lg:px-10">
        {children}
      </div>
    </section>
  )
}

export function Eyebrow({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <p
      className={`font-mono text-[10px] font-medium tracking-[0.18em] uppercase sm:text-[10.5px] ${className}`}
    >
      {children}
    </p>
  )
}

export function H2({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <h2
      className={`mt-3 font-heading text-[clamp(25px,3.4vw,42px)] leading-[1.08] font-semibold tracking-[-0.025em] text-balance sm:mt-4 ${className}`}
    >
      {children}
    </h2>
  )
}

export function Lede({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <p
      className={`mt-3 max-w-[62ch] text-[14.5px] leading-[1.6] text-pretty sm:mt-4 sm:text-[16px] lg:text-[17px] ${className}`}
    >
      {children}
    </p>
  )
}

const PILL_TONE: Record<string, string> = {
  line: "border-sales-line-strong text-sales-text bg-transparent",
  ink: "border-white/16 bg-white/8 text-[#dbe7ec]",
  teal: "border-secondary-100 bg-secondary-50 text-secondary-700",
  pink: "border-primary-100 bg-primary-50 text-primary-600",
  amber: "border-accent-100 bg-accent-50 text-accent-700",
}

export function Pill({
  children,
  tone = "line",
  className = "",
}: {
  children: ReactNode
  tone?: "line" | "ink" | "teal" | "pink" | "amber"
  className?: string
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium whitespace-nowrap sm:text-[11.5px] ${PILL_TONE[tone]} ${className}`}
    >
      {children}
    </span>
  )
}

export function Stars({ n = 5, size = 11 }: { n?: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <SalesIcon.star
          key={i}
          width={size}
          height={size}
          className={i < n ? "text-accent-500" : "text-[#e3ddde]"}
        />
      ))}
    </div>
  )
}

const AVATAR_PALETTE = ["#b6379b", "#5fc3c4", "#eba84b", "#7c3aed", "#0ea5e9"]

export function InitialsAvatar({
  name,
  size = 40,
}: {
  name: string
  size?: number
}) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
  return (
    <div
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        background: AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length],
        fontSize: size * 0.36,
      }}
    >
      {initials}
    </div>
  )
}

const CTA_MIN_HEIGHT = { sm: 44, md: 50, lg: 56 } as const

export function CtaButton({
  children = "Quero me inscrever",
  sub,
  href = "#oferta",
  size = "lg",
  full = false,
}: {
  children?: ReactNode
  sub?: string
  href?: string
  size?: "sm" | "md" | "lg"
  full?: boolean
}) {
  return (
    <a
      href={href}
      className={`group inline-flex flex-col items-center justify-center rounded-xl bg-primary-600 px-6 py-[9px] font-semibold tracking-[-0.01em] text-white shadow-[0_8px_22px_-10px_rgba(198,6,131,.65)] transition-transform active:scale-[0.98] ${full ? "w-full" : ""}`}
      style={{ minHeight: CTA_MIN_HEIGHT[size] }}
    >
      <span className="flex items-center gap-2 text-[15px] sm:text-[16px]">
        {children}
        <SalesIcon.arrow
          width={17}
          height={17}
          className="transition-transform duration-[180ms] group-hover:translate-x-0.5"
        />
      </span>
      {sub && (
        <span className="mt-0.5 text-[11.5px] font-normal opacity-80">
          {sub}
        </span>
      )}
    </a>
  )
}
